/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

 //document.addEventListener('on:cart:after-merge', (event) => {
   // your code here
   //debugger;

 //});


 document.addEventListener('on:cart:add', (event) => {
  // your code here
  //debugger;
  document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
   bubbles: true
   }));
});

// Make star ratings clickable to scroll to review summary or Okendo reviews section
window.initStarRatingClickHandler = function() {
  console.log('Initializing intelligent star rating click handler');
  
  // Add click handler to star ratings on PDP
  const starRatings = document.querySelector('.theme-product-reviews');
  
  if (starRatings) {
    // Remove any existing click handlers to avoid duplicates
    const newStarRatings = starRatings.cloneNode(true);
    starRatings.parentNode.replaceChild(newStarRatings, starRatings);
    
    // Make the star ratings appear clickable
    newStarRatings.style.cursor = 'pointer';
    
    newStarRatings.addEventListener('click', function() {
      scrollToReviews();
    });
  }
  
  // Add click handler to the READ CUSTOMER REVIEWS button
  const readReviewsBtn = document.querySelector('#read-customer-reviews-btn');
  if (readReviewsBtn) {
    readReviewsBtn.addEventListener('click', function() {
      scrollToOkendoReviews();
    });
  }
};

// Helper function to scroll to reviews (intelligent routing)
function scrollToReviews() {
  // First check if Review Summary section exists
  const reviewSummarySection = document.querySelector('.review-summary');
  
  if (reviewSummarySection) {
    // If Review Summary exists, scroll to it
    scrollToElement(reviewSummarySection);
  } else {
    // If no Review Summary, scroll directly to Okendo reviews
    scrollToOkendoReviews();
  }
}

// Helper function to scroll specifically to Okendo reviews
function scrollToOkendoReviews() {
  // Find the Okendo reviews section - it's typically in a section with the app block
  const okendoSection = document.querySelector('[id*="17125831381d028ade"]') || 
                       document.querySelector('[data-oke-container]') ||
                       document.querySelector('.okeReviews') ||
                       document.querySelector('[class*="okendo"]');
  
  if (okendoSection) {
    scrollToElement(okendoSection);
  } else {
    console.log('Okendo reviews section not found');
  }
}

// Helper function to handle smooth scrolling with header offset
function scrollToElement(element) {
  if (element) {
    // Calculate scroll position accounting for sticky header
    const headerHeight = theme.stickyHeaderHeight ? theme.stickyHeaderHeight() : 0;
    const elementTop = element.getBoundingClientRect().top + window.pageYOffset;
    const offsetPosition = elementTop - headerHeight - 20; // 20px buffer
    
    // Smooth scroll to the element
    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  }
}

// Initialize on DOM load (for full HTTP page loads)
document.addEventListener('DOMContentLoaded', function() {
  window.initStarRatingClickHandler();
  // Wait for carousel to be initialized
  setTimeout(function() {
    window.initMobileGalleryDots();
  }, 500);
});

/**
 * Mobile Gallery Dots Navigation
 * Handles dot indicator clicks for mobile gallery navigation
 */
window.initMobileGalleryDots = function() {
  const mediaGallery = document.querySelector('.mobile-gallery--dots');
  
  if (!mediaGallery || window.innerWidth > 749) {
    return; // Exit if not mobile or not using dots style
  }
  
  const dots = mediaGallery.querySelectorAll('.slider-dots__dot');
  const mainCarousel = mediaGallery.querySelector('.main-image carousel-slider');
  const slider = mainCarousel ? mainCarousel.querySelector('.slider') : null;
  const sliderItems = mediaGallery.querySelectorAll('.main-image .slider__item');
  
  if (!dots.length || !mainCarousel || !sliderItems.length || !slider) {
    console.log('Mobile gallery dots: Missing required elements');
    return;
  }
  
  // Prevent duplicate initialization
  if (slider.dataset.dotsInitialized === 'true') {
    return;
  }
  slider.dataset.dotsInitialized = 'true';
  
  console.log('Initializing mobile gallery dots with', dots.length, 'dots and', sliderItems.length, 'slides');
  
  // Store new dots array that will be used throughout
  const activeDots = [];
  
  // Function to update dots based on current scroll position
  function updateDots() {
    const slideWidth = sliderItems[0].offsetWidth + 15; // Include gap
    const scrollLeft = slider.scrollLeft;
    const activeIndex = Math.round(scrollLeft / slideWidth);
    
    activeDots.forEach((dot, index) => {
      if (index === activeIndex) {
        dot.classList.add('is-active');
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.classList.remove('is-active');
        dot.setAttribute('aria-current', 'false');
      }
    });
    
    // Update slide active states
    sliderItems.forEach((item, index) => {
      if (index === activeIndex) {
        item.classList.add('is-active');
        item.removeAttribute('tabindex');
      } else {
        item.classList.remove('is-active');
        item.setAttribute('tabindex', '-1');
      }
    });
  }
  
  // Handle dot clicks
  dots.forEach((dot, index) => {
    // Remove any existing listeners first by cloning
    const newDot = dot.cloneNode(true);
    dot.parentNode.replaceChild(newDot, dot);
    activeDots.push(newDot); // Add to our active dots array
    
    newDot.addEventListener('click', function(evt) {
      evt.preventDefault();
      evt.stopPropagation();
      
      console.log('Dot clicked:', index);
      
      const targetSliderItem = sliderItems[index];
      if (!targetSliderItem) return;
      
      // Calculate exact scroll position
      const slideWidth = targetSliderItem.offsetWidth + 15; // Include gap
      const scrollPosition = slideWidth * index;
      
      console.log('Scrolling to position:', scrollPosition);
      
      slider.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      });
      
      // Update dots immediately using activeDots array
      activeDots.forEach((d, i) => {
        if (i === index) {
          d.classList.add('is-active');
          d.setAttribute('aria-current', 'true');
        } else {
          d.classList.remove('is-active');
          d.setAttribute('aria-current', 'false');
        }
      });
    });
  });
  
  // Listen for scroll events to update dots
  let scrollTimeout;
  slider.addEventListener('scroll', function() {
    clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(updateDots, 50);
  });
  
  // Initial dot state
  updateDots();
  
  console.log('Mobile gallery dots initialized successfully');
};

// Re-initialize when variant changes (in case media changes)
document.addEventListener('on:variant:change', function() {
  setTimeout(() => {
    window.initMobileGalleryDots();
  }, 100);
});






